import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, ShieldOff, Loader2, Copy, Download, AlertTriangle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../api';
import { PasswordInput } from './PasswordInput';
import { formatApiError } from '../lib/apiError';

/**
 * Iter-46ah — MFA management card for Profile / Settings.
 *
 * Reads /auth/mfa/status to decide which state to render:
 *   - Not enabled, role doesn't require: simple "Enable" button + benefit copy
 *   - Not enabled, role requires + grace > 0: orange warning + Enable CTA
 *   - Enabled: green confirmation + Disable button (requires password)
 *
 * Enrollment opens a dialog that walks through QR → first code → backup codes.
 */
export const MFASettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(null); // { secret, provisioning_uri }
  const [enrollCode, setEnrollCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [acknowledgedBackup, setAcknowledgedBackup] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [loading, setLoading] = useState(false);

  const refreshStatus = async () => {
    try {
      const data = await apiGet('/api/auth/mfa/status');
      setStatus(data);
    } catch (e) {
      // 401 here means our session expired — surface gracefully.
      setStatus({ enabled: false, role_requires_mfa: false });
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const startEnrollment = async () => {
    setLoading(true);
    try {
      const data = await apiPost('/api/auth/mfa/setup', { body: {} });
      setEnrolling(data);
      setEnrollCode('');
      setBackupCodes(null);
      setAcknowledgedBackup(false);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to start MFA enrollment'));
    } finally {
      setLoading(false);
    }
  };

  const verifyEnrollment = async () => {
    setLoading(true);
    try {
      const data = await apiPost('/api/auth/mfa/verify-setup', { body: { code: enrollCode } });
      setBackupCodes(data.backup_codes);
      toast.success('MFA enabled!');
    } catch (e) {
      toast.error(formatApiError(e, 'Invalid code — please try again'));
    } finally {
      setLoading(false);
    }
  };

  const finishEnrollment = async () => {
    setEnrolling(null);
    setBackupCodes(null);
    setAcknowledgedBackup(false);
    await refreshStatus();
  };

  const disableMfa = async () => {
    setLoading(true);
    try {
      await apiPost('/api/auth/mfa/disable', { body: { password: disablePassword } });
      toast.success('MFA disabled');
      setShowDisableDialog(false);
      setDisablePassword('');
      await refreshStatus();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to disable MFA'));
    } finally {
      setLoading(false);
    }
  };

  const downloadCodes = () => {
    const txt = `StudioScheduler — MFA Backup Codes\nGenerated ${new Date().toISOString()}\n\nEach code can be used ONCE. Keep them somewhere safe.\n\n${backupCodes.join('\n')}\n`;
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'studioscheduler-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!status) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="mfa-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add a second login step with Google Authenticator, Microsoft Authenticator, or Authy.
              </CardDescription>
            </div>
            {status.enabled ? (
              <Badge variant="default" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                <ShieldCheck className="w-3 h-3 mr-1" /> Enabled
              </Badge>
            ) : (
              <Badge variant="outline">Disabled</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.role_requires_mfa && !status.enabled && status.grace_logins_remaining != null && (
            <Alert variant={status.grace_logins_remaining <= 2 ? 'destructive' : 'default'}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>MFA required for your role</AlertTitle>
              <AlertDescription>
                You have <strong>{status.grace_logins_remaining}</strong> login{status.grace_logins_remaining === 1 ? '' : 's'} remaining before MFA enrollment is mandatory.
              </AlertDescription>
            </Alert>
          )}

          {status.enabled ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDisableDialog(true)}
                data-testid="mfa-disable-btn"
              >
                <ShieldOff className="w-4 h-4 mr-2" /> Disable MFA
              </Button>
              {status.enrolled_at && (
                <p className="text-xs text-muted-foreground self-center">
                  Enrolled on {new Date(status.enrolled_at).toLocaleDateString()}
                </p>
              )}
            </div>
          ) : (
            <Button onClick={startEnrollment} disabled={loading} data-testid="mfa-enable-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Enable MFA
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Enrollment dialog */}
      <Dialog open={!!enrolling} onOpenChange={(o) => { if (!o) finishEnrollment(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{backupCodes ? 'Save your backup codes' : 'Set up two-factor authentication'}</DialogTitle>
            <DialogDescription>
              {backupCodes
                ? 'Each code can be used once if you lose access to your authenticator.'
                : 'Scan the QR code with your authenticator app, then enter the 6-digit code below.'}
            </DialogDescription>
          </DialogHeader>

          {!backupCodes && enrolling && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg border">
                <QRCodeSVG value={enrolling.provisioning_uri} size={180} level="M" />
              </div>
              <div className="text-xs text-muted-foreground text-center">
                Can't scan? Enter this secret manually:
                <div className="font-mono mt-1 select-all break-all" data-testid="mfa-settings-secret">{enrolling.secret}</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mfa-settings-code">6-digit code from your app</Label>
                <Input
                  id="mfa-settings-code"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="\d{6}"
                  value={enrollCode}
                  onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  data-testid="mfa-settings-code-input"
                />
              </div>
              <Button
                onClick={verifyEnrollment}
                disabled={loading || enrollCode.length !== 6}
                className="w-full"
                data-testid="mfa-settings-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
              </Button>
            </div>
          )}

          {backupCodes && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Save these now</AlertTitle>
                <AlertDescription>You'll never see them again.</AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm" data-testid="mfa-settings-backup-codes">
                {backupCodes.map((c) => <div key={c} className="text-center select-all">{c}</div>)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success('Copied'); }}>
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
                <Button variant="outline" className="flex-1" onClick={downloadCodes}>
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgedBackup}
                  onChange={(e) => setAcknowledgedBackup(e.target.checked)}
                  className="mt-1"
                />
                <span>I've saved my backup codes</span>
              </label>
              <Button disabled={!acknowledgedBackup} className="w-full" onClick={finishEnrollment} data-testid="mfa-settings-done-btn">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication?</DialogTitle>
            <DialogDescription>
              Re-enter your password to confirm. If your role requires MFA, you'll need to set it up again soon.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="mfa-disable-pw">Password</Label>
            <PasswordInput
              id="mfa-disable-pw"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              autoFocus
              data-testid="mfa-disable-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={disableMfa} disabled={loading || !disablePassword} data-testid="mfa-disable-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
